import { oktaAuth } from "../contexts/UserContext";
import { EYEBALL_IFRAME_ID, ItemType } from "../constants/EyeballConstants";
import {
  AddItemsPayload,
  AuthenticatorItemReview,
  AuthenticatorOtherItemReview,
  AuthInfo,
  EnterEditModePayload,
  EyeballPostMsgType,
  EyeballTrainingType,
  IframePostMsgBody,
} from "../types/Eyeball";

export const isGameUsedBall = (itemType?: string) =>
  itemType ? itemType.localeCompare(ItemType.GAME_USED_BALL, "en", { sensitivity: "base" }) === 0 : false;

export const isGameUsedBatOrBrokenBat = (itemType?: string) =>
  itemType
    ? itemType.localeCompare(ItemType.GAME_USED_BAT, "en", { sensitivity: "base" }) === 0 ||
      itemType.localeCompare(ItemType.GAME_USED_BROKEN_BAT, "en", { sensitivity: "base" }) === 0
    : false;

export const sortReviewsByHologramThenItemId = (
  a: AuthenticatorItemReview | AuthenticatorOtherItemReview,
  b: AuthenticatorItemReview | AuthenticatorOtherItemReview,
) => {
  const aHologram = a.review.hologram;
  const bHologram = b.review.hologram;

  if (aHologram && bHologram) {
    return aHologram.localeCompare(bHologram);
  }

  return a.review.itemId - b.review.itemId;
};

export const sendIframeAuthMsg = async (training?: EyeballTrainingType) => {
  const authInfo = await getAuthInfo();
  const postMsgBody: IframePostMsgBody = {
    type: EyeballPostMsgType.AUTH,
    training,
    authInfo,
  };

  sendIframeMsg(postMsgBody);
};

export const sendIframeTrainingMsg = async (training: EyeballTrainingType) => {
  const authInfo = await getAuthInfo();
  const postMsgBody: IframePostMsgBody = {
    type: EyeballPostMsgType.TRAINING,
    authInfo,
    training,
  };

  sendIframeMsg(postMsgBody);
};

export const sendIframeEditMsg = async ({
  sessionAuthenticatorId,
  sessionId,
  gamePk,
  editItemId,
  scrollToEntityType,
  scrollToIndex,
  training,
}: EnterEditModePayload) => {
  const authInfo = await getAuthInfo();
  const postMsgBody: IframePostMsgBody = {
    type: EyeballPostMsgType.EDIT,
    authInfo,
    sessionAuthenticatorId,
    sessionId,
    gamePk,
    editItemId,
    scrollToEntityType,
    scrollToIndex,
    training,
  };

  sendIframeMsg(postMsgBody);
};

export const sendIframeAddItemsModeMsg = async ({
  sessionAuthenticatorId,
  sessionId,
  gamePk,
  training,
}: AddItemsPayload) => {
  const authInfo = await getAuthInfo();
  const postMsgBody: IframePostMsgBody = {
    type: EyeballPostMsgType.ADD_ITEMS,
    authInfo,
    sessionAuthenticatorId,
    sessionId,
    gamePk,
    training,
  };

  sendIframeMsg(postMsgBody);
};

export const sendIframeResetEditModeMsg = async () => {
  const authInfo = await getAuthInfo();
  const postMsgBody: IframePostMsgBody = {
    type: EyeballPostMsgType.RESET_EDIT_MODE,
    authInfo: authInfo,
  };

  sendIframeMsg(postMsgBody);
};

const getAuthInfo = async (): Promise<AuthInfo> => {
  const authenticated = await oktaAuth.isAuthenticated();

  if (authenticated) {
    const authState = oktaAuth.authStateManager.getAuthState();
    return {
      email: authState?.accessToken?.claims.email ?? "",
      accessToken: authState?.accessToken?.accessToken ?? "",
      accessTokenIssuedAtInSeconds: authState?.accessToken?.claims.iat ?? 0,
      accessTokenExpiresInSeconds:
        authState?.accessToken?.expiresAt !== undefined && authState?.accessToken?.claims.iat !== undefined
          ? authState.accessToken.expiresAt - authState.accessToken.claims.iat
          : undefined,
      refreshToken: authState?.refreshToken?.refreshToken ?? "",
    };
  }

  throw new Error("Error getting token info. Please try again or contact MLB.");
};

const sendIframeMsg = (postMsgBody: IframePostMsgBody) => {
  const iframe = document.getElementById(EYEBALL_IFRAME_ID) as HTMLIFrameElement | null;
  iframe?.contentWindow?.postMessage(postMsgBody, window.MLBBest.envVariables.EYEBALL_APP_URL ?? "");
};
